// Conversation view — indexed by conversationId, not draftId.
//
// Shows the full message history for a contact, plus the most recent
// pending draft at the bottom (if any). When there's no pending draft,
// renders just the history with a placeholder. Linked to from the Inbox
// rows. Slack/Console deep-links to a specific draft still go through
// /thread/:draftId.

import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Appointment, Conversation as ConversationT, Draft, Lead, Message, QualificationSignal } from "../lib/types";
import { LeadStatusPill, EyebrowPill } from "../components/StatusPill";
import { ConfidenceBadge, LowConfidenceBanner } from "../components/ConfidenceBadge";
import { ConversationStepper } from "../components/ConversationStepper";
import { ChatBubble } from "../components/ChatBubble";

interface Loaded {
  conversation: ConversationT & { leads: Lead };
  messages: Message[];
  signals: QualificationSignal[];
  pendingDraft: Draft | null;
  // Most recent draft regardless of status — used to keep the stepper
  // showing the latest stage even after the pending draft was sent and
  // we're waiting on the lead's reply.
  latestDraft: Draft | null;
  // Distinct next_expected_step values from prior drafts, for stepper
  // history coloring.
  passedStages: string[];
  // Active appointment booked for this lead, if any. Source of truth
  // for "call confirmed" — set by applyDraftAnalysis's bookAppointment
  // success path. When present, the stepper is forced to "booking" and
  // the editor renders a compose-only banner (no Charles drafts auto-
  // generated; webhook also short-circuits).
  activeAppointment: Appointment | null;
}

export default function ConversationPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<Loaded | null>(null);
  const [editedBubbles, setEditedBubbles] = useState<string[]>([]);
  const [editFeedback, setEditFeedback] = useState("");
  const [composeBubbles, setComposeBubbles] = useState<string[]>([""]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  async function load() {
    setLoading(true);
    const { data: conv } = await supabase
      .from("conversations")
      .select(`*, leads (*)`)
      .eq("id", conversationId)
      .single();
    if (!conv) {
      setLoading(false);
      return;
    }

    // Fetch active appointment alongside everything else — defines the
    // "call booked" mode for this conversation.
    const sevenDaysAgoIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const [
      { data: msgs },
      { data: sigs },
      { data: allDrafts },
      { data: appts },
    ] = await Promise.all([
      supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", conv.id)
        .order("sent_at", { ascending: true }),
      supabase
        .from("qualification_signals")
        .select("*")
        .eq("lead_id", (conv as { leads: Lead }).leads.id)
        .order("detected_at", { ascending: false }),
      // All drafts for this conversation, newest first. We pick:
      //   - latestDraft = drafts[0] (any status) → drives the stepper
      //   - pendingDraft = first one with status='pending' → drives the editor
      //   - passedStages = distinct next_expected_step from older drafts
      supabase
        .from("drafts")
        .select("*")
        .eq("conversation_id", conv.id)
        .order("created_at", { ascending: false })
        .limit(50),
      supabase
        .from("appointments")
        .select("*")
        .eq("lead_id", (conv as { leads: Lead }).leads.id)
        .gte("scheduled_at", sevenDaysAgoIso)
        .not("status", "in", "(no_show,closed_lost,closed_won,rescheduled)")
        .order("scheduled_at", { ascending: false })
        .limit(1),
    ]);
    const activeAppointment = ((appts ?? [])[0] ?? null) as Appointment | null;

    const drafts = (allDrafts ?? []) as Array<Draft & { next_expected_step?: string | null }>;
    const latestDraft = drafts[0] ?? null;

    // A pending draft is "stale" if an outbound was sent after it was
    // created — meaning Louis already replied, so this draft is now an
    // orphan from a prior round. Hide it from the editor.
    const lastOutbound = (msgs ?? [])
      .filter((m) => m.direction === "outbound")
      .sort((a, b) => b.sent_at.localeCompare(a.sent_at))[0];
    const rawPending = drafts.find((d) => d.status === "pending") ?? null;
    const pendingIsFresh =
      !!rawPending && (!lastOutbound || rawPending.created_at > lastOutbound.sent_at);
    const pendingDraft = pendingIsFresh ? rawPending : null;

    const passedStages = Array.from(
      new Set(
        drafts
          .slice(1) // exclude the latest — it's the "current"
          .map((d) => d.next_expected_step)
          .filter((s): s is string => !!s),
      ),
    );

    setData({
      conversation: conv as Loaded["conversation"],
      messages: (msgs ?? []) as Message[],
      signals: (sigs ?? []) as QualificationSignal[],
      pendingDraft,
      latestDraft,
      passedStages,
      activeAppointment,
    });
    setEditedBubbles((pendingDraft?.draft_bubbles as string[] | undefined) ?? []);
    setLoading(false);
    // NOTE: opening the page does NOT mark the conversation as read.
    // Read flag is cleared only when Louis explicitly clicks "✓ Lu" in
    // the inbox or makes a decision on the draft (Send/Edit/Skip/etc).
  }

  useEffect(() => {
    if (!conversationId) return;
    load();
    // Subscribe to live updates on this conversation:
    //   - new messages (lead replies, manual GHL replies mirrored by webhook)
    //   - draft status changes (Charles drafts a new one, or our send flips
    //     a pending draft to sent/edited)
    // Reload the whole page state on any matching change. Cheap because
    // load() does ~3 queries and there are usually <50 messages.
    const channel = supabase
      .channel(`conversation-${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => load(),
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "drafts",
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => load(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  if (loading) return <div className="p-8 text-text-muted">Loading...</div>;
  if (!data)
    return (
      <div className="flex h-full items-center justify-center text-text-muted">
        Conversation introuvable.
      </div>
    );

  const { conversation, messages, signals, pendingDraft, latestDraft, passedStages, activeAppointment } = data;
  const lead = conversation.leads;
  const wasModified = pendingDraft
    ? JSON.stringify(editedBubbles) !== JSON.stringify(pendingDraft.draft_bubbles)
    : false;
  const confidence = pendingDraft
    ? (pendingDraft as unknown as { confidence?: number }).confidence
    : undefined;
  // Stepper currentStage priority:
  //   1. Manual pin (Louis-controlled, highest priority)
  //   2. Active appointment → force "booking" (call confirmed,
  //      stepper should freeze here regardless of latest draft)
  //   3. Latest draft's next_expected_step
  //   4. nothing
  const latestDraftStage =
    (latestDraft as unknown as { next_expected_step?: string | null } | null)?.next_expected_step ??
    null;
  const currentStage =
    conversation.pinned_stage ??
    (activeAppointment ? "booking" : null) ??
    latestDraftStage;
  const isPinned = !!conversation.pinned_stage;

  // Meta/Instagram 24h messaging window: you can't send a message if the
  // lead's last inbound is older than 24h (and FB Messenger has the same
  // rule). Compute this client-side to warn before Louis clicks Send.
  // The actual block lives at GHL/Meta — we just give early feedback so
  // he doesn't waste a click. FB has a slightly different window in
  // practice but treating both as 24h is the conservative bar.
  const lastInbound = messages
    .filter((m) => m.direction === "inbound")
    .sort((a, b) => b.sent_at.localeCompare(a.sent_at))[0];
  const HOUR_24_MS = 24 * 60 * 60 * 1000;
  const HOUR_23_MS = 23 * 60 * 60 * 1000;
  const sinceLastInboundMs = lastInbound
    ? Date.now() - new Date(lastInbound.sent_at).getTime()
    : Infinity;
  const windowExpired = sinceLastInboundMs > HOUR_24_MS;
  const windowNearExpiry = !windowExpired && sinceLastInboundMs > HOUR_23_MS;

  async function pinStage(stage: string | null) {
    // Optimistic update so the stepper repaints instantly.
    setData((prev) =>
      prev
        ? {
            ...prev,
            conversation: { ...prev.conversation, pinned_stage: stage },
          }
        : prev,
    );
    await supabase
      .from("conversations")
      .update({ pinned_stage: stage })
      .eq("id", conversation.id);
  }

  async function performCompose() {
    if (acting) return;
    const cleaned = composeBubbles.map((b) => b.trim()).filter((b) => b.length > 0);
    if (cleaned.length === 0) return;
    setActing(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/console-actions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              (await supabase.auth.getSession()).data.session?.access_token
            }`,
          },
          body: JSON.stringify({
            action: "compose",
            conversationId: conversation.id,
            bubbles: cleaned,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      // Same housekeeping as performAction: clear unread, return to inbox.
      await supabase
        .from("conversations")
        .update({ last_read_at: new Date().toISOString() })
        .eq("id", conversation.id);
      navigate("/");
    } catch (e) {
      alert(`Send failed: ${(e as Error).message}`);
    } finally {
      setActing(false);
    }
  }

  async function performAction(
    action: "send" | "edit" | "skip" | "disqualify" | "ignore" | "send_and_disqualify",
  ) {
    if (!pendingDraft || acting) return;
    setActing(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/console-actions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              (await supabase.auth.getSession()).data.session?.access_token
            }`,
          },
          body: JSON.stringify({
            draftId: pendingDraft.id,
            action,
            // Send-style actions need the bubbles to push to GHL.
            bubbles:
              action === "send" || action === "edit" || action === "send_and_disqualify"
                ? editedBubbles
                : undefined,
            // wasModified flips the draft.status to "edited" instead of "sent" —
            // applies to any path that actually sends.
            wasModified:
              action === "edit" || action === "send_and_disqualify" ? wasModified : false,
            // Why explanation rides along on any edit-flavored send.
            feedback:
              (action === "edit" || action === "send_and_disqualify") &&
              editFeedback.trim().length > 0
                ? editFeedback.trim()
                : undefined,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      // Any decision (send/edit/skip/disqualify/ignore/send_and_disqualify)
      // is also "I have dealt with this" — clear the unread flag so the
      // row leaves the Pending filter cleanly. Await before navigating so
      // the Inbox load on the next page sees the up-to-date last_read_at
      // (was a fire-and-forget race before).
      await supabase
        .from("conversations")
        .update({ last_read_at: new Date().toISOString() })
        .eq("id", pendingDraft.conversation_id);
      navigate("/");
    } catch (e) {
      alert(`Action failed: ${(e as Error).message}`);
    } finally {
      setActing(false);
    }
  }

  return (
    <div className="grid h-full grid-cols-[minmax(0,1fr)_360px]">
      {/* Center pane */}
      <div className="flex flex-col overflow-hidden">
        {/* Stepper */}
        <div className="border-b border-border bg-bg-elevated/40 px-6 py-3">
          <div className="mb-2 flex items-center justify-between gap-3">
            <Link to="/" className="text-xs text-text-muted hover:text-text">
              ← Inbox
            </Link>
            <div className="flex items-center gap-2 text-[11px]">
              {currentStage && (
                <>
                  <span className="text-text-dim">Stage:</span>
                  <span className="rounded bg-bg-elevated px-2 py-0.5 font-mono text-text-muted">
                    {currentStage}
                  </span>
                </>
              )}
              {isPinned && (
                <button
                  onClick={() => pinStage(null)}
                  className="rounded bg-warn/10 px-2 py-0.5 text-warn ring-1 ring-warn/30 hover:bg-warn/20"
                  title="Unpin (Charles reprend le contrôle)"
                >
                  📌 unpin
                </button>
              )}
            </div>
          </div>
          <ConversationStepper
            currentStage={currentStage}
            passedStages={passedStages}
            pinned={isPinned}
            onPinStage={pinStage}
          />
        </div>

        {/* Conversation */}
        <div className="flex-1 overflow-auto px-6 py-5">
          <ul className="space-y-4">
            {messages.map((m) => (
              <li key={m.id}>
                <ChatBubble message={m} />
              </li>
            ))}
          </ul>
        </div>

        {/* Editor area routing:
            1. activeAppointment → force compose mode + banner. Don't show a
               Charles draft even if a stale one exists pre-booking — the
               call is the source of truth from here on.
            2. pendingDraft → standard draft editor.
            3. Otherwise → compose mode. */}
        {/* Draft editor (only when there's a pending draft AND no active call booked) */}
        {pendingDraft && !activeAppointment ? (
          <div className="border-t border-border bg-bg-elevated/60 p-4 backdrop-blur">
            {windowExpired && (
              <div className="mb-3 rounded-lg border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
                <div>
                  ⚠️ <strong>Fenêtre IG/FB 24h expirée.</strong> Meta bloque
                  l'envoi via API quand le lead n'a pas répondu depuis +24h. La
                  GHL UI réussit parfois grâce à l'exemption "Human Agent" que
                  Meta donne aux actions UI directes.
                </div>
                {conversation.ghl_conversation_id && (
                  <a
                    href={`https://app.gohighlevel.com/v2/location/${
                      // GHL location id is embedded in the env-known URL pattern
                      import.meta.env.VITE_GHL_LOCATION_ID ?? "1SI90MHQw7VpGIep7feL"
                    }/conversations/conversations/${conversation.ghl_conversation_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-warn/50 bg-warn/10 px-2.5 py-1 text-[11px] font-medium hover:bg-warn/20"
                  >
                    ↗ Ouvrir dans GHL (workaround)
                  </a>
                )}
              </div>
            )}
            {windowNearExpiry && (
              <div className="mb-3 rounded-lg border border-warn/30 bg-warn/5 px-3 py-2 text-xs text-warn/90">
                ⏳ Fenêtre IG/FB expire dans moins d'une heure. Send maintenant ou attends sa réponse.
              </div>
            )}
            {confidence !== undefined && confidence !== null && confidence < 0.55 && (
              <div className="mb-3">
                <LowConfidenceBanner confidence={confidence} />
              </div>
            )}
            {(() => {
              // Booking preview banner — when Charles set appointment_to_book,
              // surface the resolved date/time so Louis can spot-check the
              // weekday before approving. Same role as the Slack preview block.
              const apt = (pendingDraft as unknown as {
                appointment_to_book?: {
                  scheduled_at: string;
                  duration_min?: number;
                  closer?: string;
                  raw_quote?: string;
                } | null;
              }).appointment_to_book;
              if (!apt?.scheduled_at) return null;
              let rendered = apt.scheduled_at;
              try {
                rendered = new Date(apt.scheduled_at).toLocaleString("fr-CA", {
                  timeZone: "America/Montreal",
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                });
              } catch {
                /* keep raw */
              }
              return (
                <div className="mb-3 rounded-lg border border-accent-500/40 bg-accent-500/10 px-3 py-2 text-xs text-accent-200">
                  <div className="font-semibold">🗓️ Si tu approuves, ce sera booké:</div>
                  <div className="mt-1 text-sm font-medium text-accent-100">
                    {rendered} ({apt.duration_min ?? 30} min) avec {apt.closer ?? "Ian"}
                  </div>
                  {apt.raw_quote && (
                    <div className="mt-1 italic text-accent-300/80">
                      Lead a dit: "{apt.raw_quote.slice(0, 200)}"
                    </div>
                  )}
                  <div className="mt-1 text-[10px] text-accent-300/70">
                    ⚠️ Vérifie que le jour de la semaine match avant Send.
                  </div>
                </div>
              );
            })()}
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <EyebrowPill>Draft Charles</EyebrowPill>
                {wasModified && <span className="text-[11px] text-warn">✏️ édité</span>}
              </div>
              <ConfidenceBadge confidence={confidence} />
            </div>

            <div className="space-y-2">
              {editedBubbles.map((b, i) => (
                <div key={i} className="group relative">
                  <textarea
                    value={b}
                    onChange={(e) => {
                      const next = [...editedBubbles];
                      next[i] = e.target.value;
                      setEditedBubbles(next);
                    }}
                    rows={Math.max(2, b.split("\n").length + 1)}
                    className="w-full resize-y rounded-lg border border-border bg-bg px-3.5 py-2.5 pr-10 text-sm text-text focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
                    placeholder={`Bulle ${i + 1}`}
                  />
                  {editedBubbles.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setEditedBubbles(editedBubbles.filter((_, j) => j !== i))
                      }
                      title="Supprimer cette bulle"
                      className="absolute right-2 top-2 rounded p-1 text-text-dim opacity-0 transition-opacity hover:bg-status-disqualified/10 hover:text-status-disqualified group-hover:opacity-100"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setEditedBubbles([...editedBubbles, ""])}
                className="w-full rounded-lg border border-dashed border-border px-3 py-2 text-xs text-text-dim transition-colors hover:border-accent-500/40 hover:text-accent-300"
              >
                + Ajouter une bulle
              </button>
            </div>

            {/* Why-explanation textarea — only when an edit is in progress.
                Saved on drafts.edit_feedback at send time. Future few-shot
                retrieval surfaces this so Charles learns the editorial
                principle, not just the surface diff. */}
            {wasModified && (
              <div className="mt-3">
                <label className="mb-1 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-text-dim">
                  <span>Pourquoi cette correction?</span>
                  <span className="text-text-dim/60 normal-case tracking-normal">
                    (optionnel mais aide Charles à comprendre la logique)
                  </span>
                </label>
                <textarea
                  value={editFeedback}
                  onChange={(e) => setEditFeedback(e.target.value)}
                  rows={2}
                  placeholder="Ex: on avait déjà acké ce fait, donc redondant. Ou: 1 bulle suffit ici, pas besoin de splitter."
                  className="w-full resize-y rounded-lg border border-warn/30 bg-warn/5 px-3.5 py-2 text-sm text-text placeholder:text-text-dim focus:border-warn focus:outline-none focus:ring-2 focus:ring-warn/30"
                />
              </div>
            )}

            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={() => performAction(wasModified ? "edit" : "send")}
                disabled={acting}
                className="rounded-lg bg-accent-500 px-4 py-2 text-sm font-semibold text-white shadow-glow transition-opacity hover:bg-accent-600 disabled:opacity-50"
              >
                {acting ? "..." : wasModified ? "✏️ Send edited" : "✅ Send as-is"}
              </button>
              <button
                onClick={() => performAction("skip")}
                disabled={acting}
                className="rounded-lg border border-border px-4 py-2 text-sm text-text-muted hover:bg-bg-hover hover:text-text disabled:opacity-50"
              >
                ⏭ Skip
              </button>
              {/* Disqualify mode-shifts when Louis has typed something:
                  - clean draft → 🚫 Disqualify (no send, just flip status)
                  - edited draft → 🚫 Send & Disqualify (sends the message
                    THEN disqualifies the lead, e.g. polite goodbye) */}
              <button
                onClick={() =>
                  performAction(wasModified ? "send_and_disqualify" : "disqualify")
                }
                disabled={acting}
                className="rounded-lg border border-status-disqualified/40 px-4 py-2 text-sm text-status-disqualified hover:bg-status-disqualified/10 disabled:opacity-50"
                title={
                  wasModified
                    ? "Envoyer le message + marquer le lead disqualifié"
                    : "Marquer le lead disqualifié sans envoyer"
                }
              >
                {wasModified ? "🚫 Send & Disqualify" : "🚫 Disqualify"}
              </button>
              <button
                onClick={() => {
                  if (
                    confirm(
                      "Marquer comme PAS un lead? L'agent ne draftra plus pour ce contact (team member, ami, spam, etc.)",
                    )
                  ) {
                    performAction("ignore");
                  }
                }}
                disabled={acting}
                className="ml-auto rounded-lg border border-border px-4 py-2 text-sm text-text-dim hover:bg-bg-hover hover:text-text-muted disabled:opacity-50"
                title="Pas un lead — ignore forever"
              >
                🗑️ Pas un lead
              </button>
            </div>
          </div>
        ) : (
          /* No fresh pending draft — compose mode. Louis can send manual
             outbounds without a Charles draft (e.g. follow-up after a
             sent draft, or a fresh nudge while waiting on the lead). */
          <div className="border-t border-border bg-bg-elevated/60 p-4 backdrop-blur">
            {activeAppointment && (
              <div className="mb-3 rounded-lg border border-status-qualified/40 bg-status-qualified/10 px-3 py-2 text-xs text-status-qualified">
                📅 <strong>Call booké</strong> avec {activeAppointment.closer}{" "}
                le{" "}
                {new Date(activeAppointment.scheduled_at).toLocaleString("fr-CA", {
                  timeZone: "America/Montreal",
                  weekday: "long",
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                . Charles ne génère plus de drafts pour ce lead — tape ton
                message manuellement si tu veux relancer ou répondre.
              </div>
            )}
            {windowExpired && (
              <div className="mb-3 rounded-lg border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
                <div>
                  ⚠️ <strong>Fenêtre IG/FB 24h expirée.</strong> Meta bloque
                  l'envoi via API. Utilise GHL directement (l'UI a une
                  exemption Human Agent que l'API n'a pas).
                </div>
                {conversation.ghl_conversation_id && (
                  <a
                    href={`https://app.gohighlevel.com/v2/location/${
                      import.meta.env.VITE_GHL_LOCATION_ID ?? "1SI90MHQw7VpGIep7feL"
                    }/conversations/conversations/${conversation.ghl_conversation_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-warn/50 bg-warn/10 px-2.5 py-1 text-[11px] font-medium hover:bg-warn/20"
                  >
                    ↗ Ouvrir dans GHL (workaround)
                  </a>
                )}
              </div>
            )}
            {windowNearExpiry && (
              <div className="mb-3 rounded-lg border border-warn/30 bg-warn/5 px-3 py-2 text-xs text-warn/90">
                ⏳ Fenêtre IG/FB expire dans moins d'une heure.
              </div>
            )}
            <div className="mb-2 flex items-center justify-between gap-3">
              <EyebrowPill>Compose</EyebrowPill>
              <span className="text-[11px] text-text-dim italic">
                Pas de draft Charles. Tape ton propre message.
              </span>
            </div>

            <div className="space-y-2">
              {composeBubbles.map((b, i) => (
                <div key={i} className="group relative">
                  <textarea
                    value={b}
                    onChange={(e) => {
                      const next = [...composeBubbles];
                      next[i] = e.target.value;
                      setComposeBubbles(next);
                    }}
                    rows={Math.max(2, b.split("\n").length + 1)}
                    placeholder={`Bulle ${i + 1}`}
                    className="w-full resize-y rounded-lg border border-border bg-bg px-3.5 py-2.5 pr-10 text-sm text-text focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
                  />
                  {composeBubbles.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setComposeBubbles(composeBubbles.filter((_, j) => j !== i))
                      }
                      title="Supprimer cette bulle"
                      className="absolute right-2 top-2 rounded p-1 text-text-dim opacity-0 transition-opacity hover:bg-status-disqualified/10 hover:text-status-disqualified group-hover:opacity-100"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setComposeBubbles([...composeBubbles, ""])}
                className="w-full rounded-lg border border-dashed border-border px-3 py-2 text-xs text-text-dim transition-colors hover:border-accent-500/40 hover:text-accent-300"
              >
                + Ajouter une bulle
              </button>
            </div>

            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={performCompose}
                disabled={
                  acting || composeBubbles.every((b) => b.trim().length === 0)
                }
                className="rounded-lg bg-accent-500 px-4 py-2 text-sm font-semibold text-white shadow-glow transition-opacity hover:bg-accent-600 disabled:opacity-50"
              >
                {acting ? "..." : "✅ Envoyer"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Right sidebar */}
      <aside className="overflow-auto border-l border-border bg-bg-elevated/30 p-5">
        <div className="mb-5">
          <EyebrowPill>Lead</EyebrowPill>
          <div className="mt-2 font-display text-lg font-semibold tracking-display text-text">
            {lead?.full_name ?? "(unknown)"}
          </div>
          {lead && (
            <div className="mt-1.5">
              <LeadStatusPill status={lead.current_status} />
            </div>
          )}
        </div>

        <div className="mb-5 space-y-2 text-sm">
          {lead?.industry && <Field label="Industry" value={lead.industry} />}
          {lead?.business_name && <Field label="Business" value={lead.business_name} />}
          {lead?.business_revenue_estimate && (
            <Field label="Revenu" value={lead.business_revenue_estimate} />
          )}
          {lead?.team_size_estimate !== null && lead?.team_size_estimate !== undefined && (
            <Field label="Team" value={`${lead.team_size_estimate} pers`} />
          )}
          {lead?.years_in_business !== null && lead?.years_in_business !== undefined && (
            <Field label="Années" value={`${lead.years_in_business} an${lead.years_in_business > 1 ? "s" : ""}`} />
          )}
        </div>

        {pendingDraft?.reasoning && (
          <div className="mb-5">
            <EyebrowPill>Reasoning</EyebrowPill>
            <p className="mt-2 rounded-lg bg-bg-panel p-3 text-sm italic leading-relaxed text-text-muted ring-1 ring-border">
              {pendingDraft.reasoning}
            </p>
          </div>
        )}

        {signals.length > 0 && (
          <div className="mb-5">
            <EyebrowPill>Signals ({signals.length})</EyebrowPill>
            <ul className="mt-2 space-y-1.5">
              {signals.slice(0, 12).map((s) => (
                <li
                  key={s.id}
                  className="rounded-lg bg-bg-panel p-2.5 text-xs ring-1 ring-border"
                >
                  <div className="font-mono text-[10px] text-accent-400">
                    {s.signal_type}
                  </div>
                  {s.signal_value && <div className="mt-0.5 text-text">{s.signal_value}</div>}
                  {s.raw_quote && (
                    <div className="mt-1 italic text-text-dim">"{s.raw_quote}"</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

      </aside>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="w-16 shrink-0 text-[11px] uppercase tracking-wider text-text-dim">
        {label}
      </span>
      <span className="text-text">{value}</span>
    </div>
  );
}
